import { FC, useEffect, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import {
  Box, Grid, Table, TableBody, TableHead, TableRow, TableCell, tableCellClasses, Button, CircularProgress, TextField,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import { useInfiniteReport } from "../../hooks/useInfiniteReport";
import ReusableReportHeader from "../Reports/ReusableReportHeader";
import LocationSelect from "./pickers/LocationSelect";
import Filters from "../Filters/Filters";

// Stock In Hand report — same pattern as the GRN / Stock Transfer reports. One row
// per StockLevel (item × location × batch): current on-hand quantity, avg purchase
// cost and the resulting stock value. Data is server-paginated (50/page) via
// infinite scroll; all filters are server-side; export pages through the full set.

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#eaecf0", color: "#000", border: "0.75px solid #d9d4d4",
    padding: 5, fontSize: "9pt", paddingLeft: "12px", fontWeight: 600, whiteSpace: "nowrap",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: "9pt", fontWeight: "normal", color: "#292d35",
    padding: 5, border: "0.75px solid #d9d4d4", paddingLeft: "12px", verticalAlign: "top",
  },
}));

const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString("en-GB") : "-");
const num = (n: any) => Number(n ?? 0);
const money = (n: any) => `₹${(Number(n) || 0).toLocaleString("en-IN")}`;
const esc = (s: any) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string));

const ALL_COLS = ["Item", "Item Code", "Location", "Batch", "Expiry", "Quantity", "Unit", "Avg Purchase Cost", "Stock Value", "Reserved", "Last Updated"];

const StockInHandReport: FC = () => {
  const navigate = useNavigate();

  const [locationId, setLocationId] = useState("");
  const [batchInput, setBatchInput] = useState("");
  const [batchNumber, setBatchNumber] = useState(""); // debounced copy of batchInput
  const [expiryFrom, setExpiryFrom] = useState("");
  const [expiryTo, setExpiryTo] = useState("");
  // Bumped on reset to remount the shared date-range dropdown back to "Select Date".
  const [filtersKey, setFiltersKey] = useState(0);

  // Debounce the batch search so we don't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setBatchNumber(batchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [batchInput]);

  // Server-side, scroll-paginated data (50 records/page).
  const { rows, total, loading, loadingMore, hasMore, loadMore, collectAll } = useInfiniteReport(
    "/api/v1/erp/stock/levels",
    { locationId, batchNumber, expiryFrom, expiryTo }
  );

  // Shared Filters dropdown emits "start=YYYY-MM-DD&end=YYYY-MM-DD" (empty for "Select Date").
  const handleDateFilter = (qs: string) => {
    const params = new URLSearchParams(qs);
    unstable_batchedUpdates(() => {
      setExpiryFrom(params.get("start") || "");
      setExpiryTo(params.get("end") || "");
    });
  };

  const resetFilters = () => {
    setLocationId(""); setBatchInput(""); setBatchNumber(""); setExpiryFrom(""); setExpiryTo("");
    setFiltersKey((k) => k + 1);
  };

  const onScroll = (e: any) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 300 && hasMore && !loadingMore && !loading) loadMore();
  };

  const rangeText = expiryFrom || expiryTo ? `Expiry ${expiryFrom ? fmtDate(expiryFrom) : "…"} – ${expiryTo ? fmtDate(expiryTo) : "…"}` : "All stock";

  // -------- export (pages through the FULL filtered set, not just what's loaded) --------
  const flatRows = (data: any[]): string[][] =>
    data.map((e: any): string[] => [
      e.skuId?.name || "", e.skuId?.itemCode || "", e.locationId?.name || "",
      e.batchId?.batchNumber || "", e.batchId?.expiryDate ? fmtDate(e.batchId.expiryDate) : "",
      String(num(e.quantity)), e.skuId?.baseUnitName || "",
      String(num(e.avgPurchaseCost)), String(num(e.quantity) * num(e.avgPurchaseCost)),
      String(num(e.reserved)), fmtDate(e.updatedAt),
    ]);

  const buildTableHtml = (data: any[]) => `
    <table border="1" cellspacing="0" cellpadding="5" style="border-collapse:collapse;font-family:Arial;font-size:11px">
      <thead><tr style="background:#eaecf0">${ALL_COLS.map((c) => `<th align="left">${esc(c)}</th>`).join("")}</tr></thead>
      <tbody>${flatRows(data).map((r) => `<tr>${r.map((v) => `<td>${esc(v)}</td>`).join("")}</tr>`).join("")}</tbody>
    </table>`;

  const handleDownloadExcel = async () => {
    const all = await collectAll();
    const html = `<html><head><meta charset="utf-8" /></head><body>${buildTableHtml(all)}</body></html>`;
    const blob = new Blob([html], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `Stock_In_Hand_Report_${new Date().toISOString().split("T")[0]}.xls`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const handleDownloadPDF = async () => {
    const all = await collectAll();
    const totalValue = all.reduce((s: number, e: any) => s + num(e.quantity) * num(e.avgPurchaseCost), 0);
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8" /><title>Stock In Hand Report</title>
      <style>@page{size:A4 landscape;margin:10mm}
      body{font-family:Arial;padding:0} h3{margin:0 0 4px} .sub{color:#666;font-size:12px;margin-bottom:12px}
      table{border-collapse:collapse;width:100%;font-size:10px} th,td{border:0.75px solid #d9d4d4;padding:4px;text-align:left}
      thead tr{background:#eaecf0}</style></head>
      <body><h3>Stock In Hand Report</h3><div class="sub">${esc(rangeText)} · ${all.length} stock lines · Value ${esc(money(totalValue))}</div>
      ${buildTableHtml(all)}</body></html>`;
    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:absolute;width:0;height:0;border:0;left:-9999px;";
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow?.document;
    if (!doc) return;
    doc.open(); doc.write(html); doc.close();
    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow?.focus(); iframe.contentWindow?.print();
        setTimeout(() => document.body.removeChild(iframe), 500);
      }, 300);
    };
  };

  // Dashboard report-card's download button opens this page with ?download=excel|pdf —
  // trigger that export once and clean the URL.
  useEffect(() => {
    const dl = new URLSearchParams(window.location.search).get("download");
    if (!dl) return;
    window.history.replaceState({}, "", window.location.pathname);
    const t = setTimeout(() => { dl === "pdf" ? handleDownloadPDF() : handleDownloadExcel(); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ width: "100%", pr: 1 }}>
      <Box sx={{ width: "100%", overflow: "hidden", mb: 2 }} className={"c-card2"}>
        <ReusableReportHeader
          reportType="stock_in_hand"
          title="Stock In Hand Report"
          subtitle={`${rangeText} · ${total} stock lines`}
          recordCount={total}
          isLoading={loading}
          backTo="/inventory-dashboard"
          showPrint={false}
          showShare={false}
          onDownloadPDF={handleDownloadPDF}
          onDownloadExcel={handleDownloadExcel}
        />

        {/* Filters strip */}
        <Grid container spacing={1.5} alignItems="center" p={2} style={{ borderBottom: ".5px solid #9da7b7" }}>
          <Grid item>
            <LocationSelect borderless width={200} value={locationId} onChange={(v: string) => setLocationId(v)} label="Location" activeOnly={false} />
          </Grid>
          <Grid item>
            <TextField
              className="c-text-field-3"
              variant="standard"
              size="small"
              placeholder="Batch #"
              value={batchInput}
              onChange={(e) => setBatchInput(e.target.value)}
              style={{ width: 150 }}
              InputProps={{ disableUnderline: true }}
            />
          </Grid>
          <Grid item>
            <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
              <span style={{ position: "absolute", top: -8, left: 20, padding: "0 5px", background: "#fff", fontSize: 12, color: "#6b7280", lineHeight: 1, zIndex: 1, pointerEvents: "none" }}>Expiry between</span>
              <Filters key={filtersKey} type="inventory-stock-in-hand" dateOnly hidePartySearch hideFilter hideReset filterHandler={handleDateFilter} />
            </div>
          </Grid>
          <Grid item>
            <Button size="small" color="inherit" startIcon={<RestartAltIcon />} onClick={resetFilters} sx={{ color: "#7a8290" }}>
              Reset
            </Button>
          </Grid>
        </Grid>

        {/* Report table — one row per stock level; scroll loads more */}
        <div onScroll={onScroll} style={{ maxHeight: "62vh", overflow: "auto" }}>
          <Table stickyHeader sx={{ minWidth: 1300 }} size="small" aria-label="stock in hand report">
            <TableHead>
              <TableRow>
                {ALL_COLS.map((c) => <StyledTableCell key={c}>{c}</StyledTableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length > 0 ? rows.map((e: any) => {
                const expired = e.batchId?.expiryDate ? new Date(e.batchId.expiryDate).getTime() <= Date.now() : false;
                return (
                  <TableRow key={e._id}>
                    <StyledTableCell>
                      <span style={{ color: "#488dff", cursor: "pointer" }} onClick={() => navigate(`/inventory/view/${e.skuId?._id || e.skuId}`)}>
                        {e.skuId?.name || "-"}
                      </span>
                    </StyledTableCell>
                    <StyledTableCell>{e.skuId?.itemCode || "-"}</StyledTableCell>
                    <StyledTableCell>{e.locationId?.name || "-"}</StyledTableCell>
                    <StyledTableCell>{e.batchId?.batchNumber || "-"}</StyledTableCell>
                    <StyledTableCell>
                      {e.batchId?.expiryDate ? (
                        <span style={{ color: expired ? "#c33b3b" : "inherit" }}>{fmtDate(e.batchId.expiryDate)}</span>
                      ) : "-"}
                    </StyledTableCell>
                    <StyledTableCell>{num(e.quantity)}</StyledTableCell>
                    <StyledTableCell>{e.skuId?.baseUnitName || "-"}</StyledTableCell>
                    <StyledTableCell>{money(e.avgPurchaseCost)}</StyledTableCell>
                    <StyledTableCell>{money(num(e.quantity) * num(e.avgPurchaseCost))}</StyledTableCell>
                    <StyledTableCell>{num(e.reserved)}</StyledTableCell>
                    <StyledTableCell>{fmtDate(e.updatedAt)}</StyledTableCell>
                  </TableRow>
                );
              }) : (
                <TableRow>
                  <StyledTableCell colSpan={ALL_COLS.length} style={{ textAlign: "center", color: "#9da7b7", padding: 24 }}>
                    {loading ? "Loading…" : "No stock in hand matches the selected filters."}
                  </StyledTableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {loadingMore ? (
            <div style={{ textAlign: "center", padding: 12 }}><CircularProgress size={20} /></div>
          ) : (!hasMore && rows.length > 0) ? (
            <div style={{ textAlign: "center", padding: 10, color: "#c7ccd4", fontSize: 12 }}>All {total} stock lines loaded</div>
          ) : null}
        </div>
      </Box>
    </Box>
  );
};

export default StockInHandReport;
